"use client";

import { TaskRenderer } from "@/components/test/TaskRenderer";
import { TestHeader } from "@/components/test/TestHeader";
import { getDuration, getSchoolYear, getTotalPoints } from "@/lib/test/documentHelpers";
import type {
  TaskAnswer,
  TaskBlock,
  TestExercise,
  TestOutput,
  TestSection,
} from "@/lib/testTypes";
import { TestSectionRefinement } from "@/components/TestSectionRefinement";
import { ensureBlocks } from "@/lib/test/migrateTaskShape";
import { cn } from "@/lib/utils";
import { ANALYTICS_EVENTS, track } from "@/lib/analyticsEvents";
import {
  APP_PRESSABLE_PRIMARY,
  APP_PRESSABLE_LINK,
  APP_PRESSABLE_SECONDARY,
} from "@/lib/app-ui";
import type { Id } from "convex/_generated/dataModel";
import { useCallback, useEffect, useRef, useState } from "react";
import type { Dispatch, SetStateAction } from "react";
import { Download, FileText, KeyRound, Pencil, Plus, Printer } from "lucide-react";

/* ------------------------------------------------------------------ */
/*  TestResult component                                               */
/* ------------------------------------------------------------------ */

export function TestResult({
  output,
  onNew,
  exportLocked = false,
  testId,
  status,
  editablePending = false,
  onOutputChange,
  stickyActions = false,
  toolbarActions,
  teacherName,
  schoolName,
  onDownloaded,
}: {
  output: TestOutput;
  onNew: () => void;
  exportLocked?: boolean;
  testId?: Id<"testGenerations">;
  status?: "pending_acceptance" | "accepted" | "analysis_ready" | "rejected";
  editablePending?: boolean;
  onOutputChange?: (next: TestOutput) => void;
  stickyActions?: boolean;
  toolbarActions?: React.ReactNode;
  teacherName?: string;
  schoolName?: string;
  onDownloaded?: (kind: "test" | "answer_key", format: "print" | "doc") => void;
}) {
  const [showAnswers, setShowAnswers] = useState(false);
  const [refiningKeys, setRefiningKeys] = useState<Record<string, boolean>>({});
  const previewTracked = useRef(false);
  const currentOutput = output;

  useEffect(() => {
    if (previewTracked.current) return;
    previewTracked.current = true;
    track(ANALYTICS_EVENTS.testPreviewViewed, {
      test_type: currentOutput.testType,
      status: status === "pending_acceptance" ? "pending_acceptance" : "accepted",
    });
  }, [currentOutput.testType, status]);

  const handleDownloadStudent = useCallback(() => {
    const html = buildTestHtml(currentOutput, false, teacherName, schoolName);
    openPrintWindow(html);
    track(ANALYTICS_EVENTS.testDownloaded, {
      test_type: currentOutput.testType,
      format: "print",
    });
    onDownloaded?.("test", "print");
  }, [currentOutput, onDownloaded, schoolName, teacherName]);

  const handleDownloadAnswerKey = useCallback(() => {
    const html = buildTestHtml(currentOutput, true, teacherName, schoolName);
    openPrintWindow(html);
    track(ANALYTICS_EVENTS.testAnswerKeyDownloaded, {
      test_type: currentOutput.testType,
      format: "print",
    });
    onDownloaded?.("answer_key", "print");
  }, [currentOutput, onDownloaded, schoolName, teacherName]);

  const handleExportStudentDoc = useCallback(() => {
    const html = buildTestHtml(currentOutput, false, teacherName, schoolName);
    downloadDoc(html, `${currentOutput.testType}-${currentOutput.grade}.doc`);
    track(ANALYTICS_EVENTS.testDownloaded, {
      test_type: currentOutput.testType,
      format: "doc",
    });
    onDownloaded?.("test", "doc");
  }, [currentOutput, onDownloaded, schoolName, teacherName]);

  const handleExportAnswerKeyDoc = useCallback(() => {
    const html = buildTestHtml(currentOutput, true, teacherName, schoolName);
    downloadDoc(
      html,
      `${currentOutput.testType}-${currentOutput.grade}-answer-key.doc`,
    );
    track(ANALYTICS_EVENTS.testAnswerKeyDownloaded, {
      test_type: currentOutput.testType,
      format: "doc",
    });
    onDownloaded?.("answer_key", "doc");
  }, [currentOutput, onDownloaded, schoolName, teacherName]);

  const typeLabel = currentOutput.testType === "quiz" ? "Quiz" : "Global Test";
  const totalPoints = getTotalPoints(currentOutput.sections);
  const duration = getDuration(currentOutput.testType);
  const schoolYear = getSchoolYear();
  const resolvedTeacherName = teacherName?.trim() ? teacherName : "—";
  const canRefine = Boolean(
    editablePending &&
      status === "pending_acceptance" &&
      testId,
  );

  return (
    <div className="flex flex-col gap-4">
      <div
        className={cn(
          "flex flex-wrap items-center gap-2 rounded-xl border border-border bg-card p-2 shadow-sm",
          stickyActions &&
            "sticky top-3 z-20 bg-card/95 shadow-md backdrop-blur-md supports-[backdrop-filter]:bg-card/90",
        )}
        aria-label="Test actions"
      >
        {toolbarActions}
        {!exportLocked ? (
          <>
            <button
              type="button"
              onClick={handleExportStudentDoc}
              className={cn(APP_PRESSABLE_PRIMARY, "min-h-10 gap-2 px-4")}
            >
              <Download className="h-4 w-4" />
              Download test
            </button>
            <button
              type="button"
              onClick={handleExportAnswerKeyDoc}
              className={cn(APP_PRESSABLE_SECONDARY, "min-h-10 gap-2 px-4")}
            >
              <FileText className="h-4 w-4" />
              Download answer key
            </button>
            <button
              type="button"
              onClick={handleDownloadStudent}
              className={cn(APP_PRESSABLE_SECONDARY, "min-h-10 gap-2 px-4")}
            >
              <Printer className="h-4 w-4" />
              Printable preview
            </button>
            <button
              type="button"
              onClick={handleDownloadAnswerKey}
              className={cn(APP_PRESSABLE_SECONDARY, "min-h-10 gap-2 px-4")}
            >
              <KeyRound className="h-4 w-4" />
              Answer key preview
            </button>
          </>
        ) : null}
        <button
          type="button"
          onClick={() => setShowAnswers((value) => !value)}
          className={cn(APP_PRESSABLE_LINK, "min-h-10 px-3 text-xs")}
        >
          {showAnswers ? "Hide answers" : "Show answers"}
        </button>
        {!exportLocked ? (
          <button
            type="button"
            onClick={onNew}
            className={cn(APP_PRESSABLE_LINK, "min-h-10 gap-2 px-4")}
          >
            <Plus className="h-4 w-4" />
            Start another test
          </button>
        ) : null}
      </div>

      {!showAnswers ? (
        <TestHeader
          testTitle={`${typeLabel} | ${currentOutput.lessonTopic}`}
          level={currentOutput.grade}
          teacherName={resolvedTeacherName}
          schoolName={schoolName}
          duration={duration}
          totalPoints={totalPoints}
          schoolYear={schoolYear}
        />
      ) : null}

      {/* Quiz note */}
      {currentOutput.testType === "quiz" && (
        <div className="rounded-xl border border-primary/20 bg-primary/5 p-3 text-xs text-muted-foreground">
          This quiz counts /10. The two semester quizzes (quiz 1 + quiz 2) will
          add to give your 20-point written assessment total.
        </div>
      )}

      {/* Sections */}
      {currentOutput.sections.map((section, sectionIndex) => (
        <SectionPreview
          key={section.number}
          section={section}
          showAnswers={showAnswers}
          canRefine={canRefine}
          testId={testId}
          sectionIndex={sectionIndex}
          refiningKeys={refiningKeys}
          setRefiningKeys={setRefiningKeys}
          onRefinedSection={(updatedSection) => {
            const sections = [...currentOutput.sections];
            sections[sectionIndex] = updatedSection;
            onOutputChange?.({ ...currentOutput, sections });
            track(ANALYTICS_EVENTS.testEdited, { scope: "section" });
            track(ANALYTICS_EVENTS.testSectionRegenerated, { scope: "section" });
          }}
          onRefinedExercise={(taskIndex, updatedExercise) => {
            const sections = [...currentOutput.sections];
            const exercises = [...sections[sectionIndex].exercises];
            exercises[taskIndex] = updatedExercise;
            sections[sectionIndex] = { ...sections[sectionIndex], exercises };
            onOutputChange?.({ ...currentOutput, sections });
            track(ANALYTICS_EVENTS.testEdited, { scope: "task" });
            track(ANALYTICS_EVENTS.testSectionRegenerated, { scope: "task" });
          }}
        />
      ))}

      {/* Writing task */}
      {currentOutput.writingTask && (
        <div className="rounded-xl border border-border p-3">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-semibold text-foreground">
              Writing Task
            </h4>
            <span className="text-xs text-muted-foreground">
              {currentOutput.writingTask.points} pts
            </span>
          </div>
          <p className="mt-2 text-sm text-foreground/85">
            {currentOutput.writingTask.instruction}
          </p>
          {currentOutput.writingTask.scaffolding && (
            <div className="mt-2 rounded-lg bg-muted/40 p-2 text-xs text-muted-foreground italic">
              {currentOutput.writingTask.scaffolding}
            </div>
          )}
          {showAnswers && (
            <div className="mt-2 rounded-lg bg-success/10 p-2 text-xs text-foreground">
              <span className="font-medium">Marking criteria: </span>
              {currentOutput.writingTask.markingCriteria}
            </div>
          )}
        </div>
      )}

    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Section preview                                                    */
/* ------------------------------------------------------------------ */

function SectionPreview({
  section,
  showAnswers,
  canRefine,
  testId,
  sectionIndex,
  refiningKeys,
  setRefiningKeys,
  onRefinedSection,
  onRefinedExercise,
}: {
  section: TestSection;
  showAnswers: boolean;
  canRefine: boolean;
  testId?: Id<"testGenerations">;
  sectionIndex: number;
  refiningKeys: Record<string, boolean>;
  setRefiningKeys: Dispatch<SetStateAction<Record<string, boolean>>>;
  onRefinedSection: (section: TestSection) => void;
  onRefinedExercise: (taskIndex: number, exercise: TestExercise) => void;
}) {
  const sectionKey = `section-${sectionIndex}`;
  const isSectionRefining = Boolean(refiningKeys[sectionKey]);
  // "section" = whole-section panel open; "ex-N" = exercise N panel open; null = closed
  const [openPanel, setOpenPanel] = useState<string | null>(null);

  const sectionContextLabel = `${section.number}. ${section.title}`;

  return (
    <div className="relative rounded-xl border border-border p-3">
      {isSectionRefining ? (
        <div className="pointer-events-none absolute inset-0 rounded-xl bg-gray-100 opacity-50 animate-pulse" />
      ) : null}

      {/* Section header row — pencil inline on the right */}
      <div className="flex items-center justify-between gap-2">
        <h4 className="text-sm font-semibold text-foreground">
          {section.number}. {section.title}
        </h4>
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">
            {section.totalPoints} pts
          </span>
          {canRefine && testId ? (
            <button
              type="button"
              onClick={() =>
                setOpenPanel((prev) => (prev === "section" ? null : "section"))
              }
              className={cn(
                "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors",
                openPanel === "section"
                  ? "border-primary/40 bg-primary/8 text-primary"
                  : "border-border bg-background text-muted-foreground hover:border-primary/30 hover:text-primary",
              )}
            >
              <Pencil className="h-3 w-3" />
              Edit section
            </button>
          ) : null}
        </div>
      </div>

      {/* Whole-section refinement panel */}
      {canRefine && testId ? (
        <TestSectionRefinement
          testId={testId}
          sectionIndex={sectionIndex}
          contextLabel={sectionContextLabel}
          isOpen={openPanel === "section"}
          onClose={() => setOpenPanel(null)}
          onLoadingChange={(loading) => {
            setRefiningKeys((prev) => ({ ...prev, [sectionKey]: loading }));
          }}
          onRefined={(updated) => {
            onRefinedSection(updated as TestSection);
            setOpenPanel(null);
          }}
        />
      ) : null}

      {/* Reading text */}
      {section.readingText && (
        <div className="mt-2 rounded-lg bg-muted/40 p-3 text-sm leading-relaxed text-foreground/85">
          {section.readingText}
        </div>
      )}

      {/* Exercises */}
      {section.exercises.map((exercise, i) => (
        <ExercisePreview
          key={i}
          exercise={exercise}
          showAnswers={showAnswers}
          canRefine={canRefine}
          testId={testId}
          sectionIndex={sectionIndex}
          taskIndex={i}
          refiningKeys={refiningKeys}
          setRefiningKeys={setRefiningKeys}
          openPanel={openPanel}
          setOpenPanel={setOpenPanel}
          onRefined={(updated) => onRefinedExercise(i, updated)}
        />
      ))}
    </div>
  );
}

function ExercisePreview({
  exercise,
  showAnswers,
  canRefine,
  testId,
  sectionIndex,
  taskIndex,
  refiningKeys,
  setRefiningKeys,
  openPanel,
  setOpenPanel,
  onRefined,
}: {
  exercise: TestExercise;
  showAnswers: boolean;
  canRefine: boolean;
  testId?: Id<"testGenerations">;
  sectionIndex: number;
  taskIndex: number;
  refiningKeys: Record<string, boolean>;
  setRefiningKeys: Dispatch<SetStateAction<Record<string, boolean>>>;
  openPanel: string | null;
  setOpenPanel: (key: string | null) => void;
  onRefined: (exercise: TestExercise) => void;
}) {
  const task = ensureBlocks(exercise as Record<string, unknown>);
  const taskKey = `task-${sectionIndex}-${taskIndex}`;
  const panelKey = `ex-${taskIndex}`;
  const isTaskRefining = Boolean(refiningKeys[taskKey]);

  const truncatedTitle =
    task.title.length > 45 ? `${task.title.slice(0, 45)}…` : task.title;
  const exerciseContextLabel = truncatedTitle;

  return (
    <div className="relative mt-3">
      {isTaskRefining ? (
        <div className="pointer-events-none absolute inset-0 rounded-lg bg-gray-100 opacity-50 animate-pulse" />
      ) : null}

      {/* Exercise header row — pencil inline on the right */}
      <div className="flex items-start justify-between gap-2 text-xs">
        <span className="font-semibold text-foreground/85">{task.title}</span>
        <div className="flex shrink-0 items-center gap-1.5">
          <span className="text-muted-foreground">{task.pointsLabel}</span>
          {canRefine && testId ? (
            <button
              type="button"
              onClick={() =>
                setOpenPanel(openPanel === panelKey ? null : panelKey)
              }
              className={cn(
                "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium transition-colors",
                openPanel === panelKey
                  ? "border-primary/40 bg-primary/8 text-primary"
                  : "border-border bg-background text-muted-foreground hover:border-primary/30 hover:text-primary",
              )}
            >
              <Pencil className="h-2.5 w-2.5" />
              Edit
            </button>
          ) : null}
        </div>
      </div>

      {/* Exercise refinement panel */}
      {canRefine && testId ? (
        <TestSectionRefinement
          testId={testId}
          sectionIndex={sectionIndex}
          taskIndex={taskIndex}
          contextLabel={exerciseContextLabel}
          isOpen={openPanel === panelKey}
          onClose={() => setOpenPanel(null)}
          onLoadingChange={(loading) => {
            setRefiningKeys((prev) => ({ ...prev, [taskKey]: loading }));
          }}
          onRefined={(updated) => {
            onRefined(updated as TestExercise);
            setOpenPanel(null);
          }}
        />
      ) : null}

      <div className="mt-1.5">
        <TaskRenderer task={task} showAnswers={showAnswers} />
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  HTML generation for print                                          */
/* ------------------------------------------------------------------ */

function openPrintWindow(html: string) {
  const iframe = document.createElement("iframe");
  iframe.style.position = "fixed";
  iframe.style.width = "0";
  iframe.style.height = "0";
  iframe.style.border = "none";
  iframe.style.left = "-9999px";
  iframe.style.top = "-9999px";
  document.body.appendChild(iframe);

  const doc = iframe.contentDocument ?? iframe.contentWindow?.document;
  if (!doc) {
    document.body.removeChild(iframe);
    return;
  }

  doc.open();
  doc.write(html);
  doc.close();

  iframe.contentWindow?.focus();
  iframe.contentWindow?.print();

  setTimeout(() => {
    document.body.removeChild(iframe);
  }, 1000);
}

function downloadDoc(html: string, fileName: string) {
  const blob = new Blob([`\uFEFF${html}`], {
    type: "application/msword;charset=utf-8",
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function answerForBlockItem(
  answers: TaskAnswer[] | undefined,
  blockIndex: number,
  itemRef: number | string,
): string | null {
  const match = answers?.find(
    (entry) => entry.blockIndex === blockIndex && entry.itemRef === itemRef,
  );
  return match ? match.answer : null;
}

function renderTaskBlockHtml(
  block: TaskBlock,
  blockIndex: number,
  answers: TaskAnswer[] | undefined,
  showAnswers: boolean,
): string {
  if (block.type === "word_box") {
    return `<div style="margin:8px 0;padding:8px 10px;background:#f7f7f7;border:1px solid #ddd;border-radius:6px;font-size:12px;">${block.words
      .map(escapeHtml)
      .join(" | ")}</div>`;
  }

  if (block.type === "numbered_list") {
    const items = block.items
      .map((item) => {
        const answer = answerForBlockItem(answers, blockIndex, item.number);
        const answerBadge =
          showAnswers && answer
            ? ` <span style="background:#d4edda;padding:2px 6px;border-radius:4px;font-size:11px;font-weight:600;color:#155724;">${escapeHtml(answer)}</span>`
            : "";
        return `<p style="font-size:12px;margin:2px 0 2px 16px;"><strong>${item.number}.</strong> ${escapeHtml(item.text)}${answerBadge}</p>`;
      })
      .join("");
    return `<div>${items}</div>`;
  }

  if (block.type === "bullet_list") {
    const items = block.items
      .map(
        (item) =>
          `<p style="font-size:12px;margin:2px 0 2px 16px;">• ${escapeHtml(item)}</p>`,
      )
      .join("");
    return `<div>${items}</div>`;
  }

  if (block.type === "table") {
    const headers = block.columns
      .map(
        (column) =>
          `<th style="border:1px solid #ddd;padding:6px;background:#f7f7f7;text-align:left;">${escapeHtml(column)}</th>`,
      )
      .join("");
    const hasHeaders = block.columns.some((column) => column.trim().length > 0);
    const rows = block.rows
      .map((row, rowIndex) => {
        const answer = answerForBlockItem(answers, blockIndex, rowIndex);
        const cells = row
          .map((cell, cellIndex) => {
            const answerSuffix =
              showAnswers && answer && cellIndex === row.length - 1
                ? ` <span style="color:#155724;font-weight:600;">→ ${escapeHtml(answer)}</span>`
                : "";
            return `<td style="border:1px solid #ddd;padding:6px;">${escapeHtml(cell)}${answerSuffix}</td>`;
          })
          .join("");
        return `<tr>${cells}</tr>`;
      })
      .join("");
    const thead = hasHeaders ? `<thead><tr>${headers}</tr></thead>` : "";
    return `<table style="width:100%;border-collapse:collapse;margin-top:6px;font-size:12px;">${thead}<tbody>${rows}</tbody></table>`;
  }

  if (block.type === "dialogue") {
    const lines = block.lines
      .map((line, lineIndex) => {
        const answer = answerForBlockItem(answers, blockIndex, lineIndex);
        const answerBadge =
          showAnswers && answer
            ? ` <span style="background:#d4edda;padding:2px 6px;border-radius:4px;font-size:11px;font-weight:600;color:#155724;">${escapeHtml(answer)}</span>`
            : "";
        return `<p style="font-size:12px;margin:2px 0 2px 16px;"><strong>${escapeHtml(line.speaker)}:</strong> ${escapeHtml(line.text)}${answerBadge}</p>`;
      })
      .join("");
    return `<div>${lines}</div>`;
  }

  if (block.type === "text") {
    return `<p style="font-size:12px;margin:4px 0;">${escapeHtml(block.content)}</p>`;
  }

  if (block.type === "reading_passage") {
    return `<div style="background:#f7f7f7;border-left:4px solid #2A6B4F;border-radius:0 6px 6px 0;padding:12px;margin:8px 0;font-size:13px;line-height:1.7;">${escapeHtml(block.content)}</div>`;
  }

  if (block.type === "print_lines") {
    const lines = Array.from({ length: block.count })
      .map(() => `<div style="border-bottom:1px solid #e0e0e0;height:20px;"></div>`)
      .join("");
    return `<div style="margin-top:8px;">${lines}</div>`;
  }

  return "";
}

function buildTestHtml(
  output: TestOutput,
  showAnswers: boolean,
  teacherName?: string,
  schoolName?: string,
): string {
  const typeLabel = output.testType === "quiz" ? "Quiz" : "Global Test";
  const totalPoints = getTotalPoints(output.sections);
  const duration = getDuration(output.testType);
  const schoolYear = getSchoolYear();
  const resolvedTeacherName = teacherName?.trim() ? teacherName : "—";
  const escapedSchoolName = schoolName?.trim() ? escapeHtml(schoolName) : "";
  const testTitle = `${typeLabel} | ${output.lessonTopic}`;
  const correctionBanner = showAnswers
    ? `<div style="text-align:center;font-size:20px;font-weight:bold;color:#c0392b;margin-bottom:16px;border:2px solid #c0392b;padding:8px;border-radius:8px;">CORRECTION — ANSWER KEY</div>`
    : "";

  let sectionsHtml = "";
  for (const section of output.sections) {
    sectionsHtml += `<div style="margin-bottom:20px;">`;
    sectionsHtml += `<h2 style="font-size:15px;margin:0 0 8px;border-bottom:2px solid #2A6B4F;padding-bottom:4px;">Section ${escapeHtml(section.number)}: ${escapeHtml(section.title)} (${section.totalPoints} pts)</h2>`;

    if (section.readingText) {
      sectionsHtml += `<div style="background:#f7f7f7;border:1px solid #ddd;border-radius:6px;padding:12px;margin-bottom:12px;font-size:13px;line-height:1.7;">${escapeHtml(section.readingText)}</div>`;
    }

    for (const exercise of section.exercises) {
      const task = ensureBlocks(exercise as Record<string, unknown>);
      sectionsHtml += `<div style="margin-bottom:14px;">`;
      sectionsHtml += `<p style="font-weight:600;font-size:13px;margin:0 0 6px;">${escapeHtml(task.title)} <span style="color:#666;font-weight:normal;">${escapeHtml(task.pointsLabel)}</span></p>`;
      for (let blockIndex = 0; blockIndex < task.blocks.length; blockIndex += 1) {
        sectionsHtml += renderTaskBlockHtml(
          task.blocks[blockIndex] as TaskBlock,
          blockIndex,
          task.answers,
          showAnswers,
        );
      }
      sectionsHtml += `</div>`;
    }
    sectionsHtml += `</div>`;
  }

  // Writing task
  if (output.writingTask) {
    sectionsHtml += `<div style="margin-bottom:20px;">`;
    sectionsHtml += `<h2 style="font-size:15px;margin:0 0 8px;border-bottom:2px solid #2A6B4F;padding-bottom:4px;">Writing (${output.writingTask.points} pts)</h2>`;
    sectionsHtml += `<p style="font-size:13px;margin:0 0 8px;">${escapeHtml(output.writingTask.instruction)}</p>`;
    if (output.writingTask.scaffolding) {
      sectionsHtml += `<div style="background:#f0f0f0;border:1px solid #ddd;border-radius:6px;padding:10px;font-size:12px;font-style:italic;margin-bottom:8px;">${escapeHtml(output.writingTask.scaffolding)}</div>`;
    }
    if (!showAnswers) {
      // Blank lines for student writing
      sectionsHtml += `<div style="border:1px solid #ddd;border-radius:6px;padding:8px;min-height:120px;">`;
      for (let i = 0; i < 8; i++) {
        sectionsHtml += `<div style="border-bottom:1px solid #e0e0e0;height:24px;"></div>`;
      }
      sectionsHtml += `</div>`;
    } else {
      sectionsHtml += `<div style="background:#d4edda;border:1px solid #c3e6cb;border-radius:6px;padding:10px;font-size:12px;"><strong>Marking criteria:</strong> ${escapeHtml(output.writingTask.markingCriteria)}</div>`;
    }
    sectionsHtml += `</div>`;
  }

  return `<!doctype html>
<html>
<head>
<meta charset="utf-8">
<title>${escapeHtml(typeLabel)} — ${escapeHtml(output.lessonTopic)}</title>
<style>
  @page { size: A4; margin: 8mm 6mm; }
  body { font-family: "Segoe UI", Arial, sans-serif; font-size: 13px; line-height: 1.5; color: #222; max-width: none; margin: 0; padding: 8px 6px; }
  .header-table { width: 100%; border-collapse: collapse; margin-bottom: 24px; font-size: 13px; }
  .header-cell { border: 1px solid #000; padding: 10px 14px; vertical-align: middle; line-height: 1.5; }
  .center-cell { text-align: center; font-weight: 600; }
  .left-cell, .right-cell { text-align: left; }
  .label { font-weight: 700; margin-right: 4px; }
  .dots { font-weight: 400; color: #333; letter-spacing: 1px; }
  .value { font-weight: 400; }
  .mark-value { font-size: 15px; font-weight: 700; }
  .school-name { text-align: center; font-size: 11px; color: #555; margin-top: -16px; margin-bottom: 20px; }
  @media print {
    body { padding: 0; }
    button { display: none !important; }
    .header-table { page-break-inside: avoid; }
    .header-cell {
      border: 1px solid #000 !important;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
  }
</style>
</head>
<body>
${correctionBanner}
${!showAnswers
    ? `<table class="header-table">
  <colgroup>
    <col style="width: 30%">
    <col style="width: 40%">
    <col style="width: 30%">
  </colgroup>
  <tbody>
    <tr>
      <td class="header-cell left-cell">
        <span class="label">Full name:</span>
        <span class="dots">................................</span>
      </td>
      <td class="header-cell center-cell">
        <strong>${escapeHtml(testTitle)}</strong>
      </td>
      <td class="header-cell right-cell">
        <span class="label">Teacher :</span>
        <span class="value">${escapeHtml(resolvedTeacherName)}</span>
      </td>
    </tr>
    <tr>
      <td class="header-cell left-cell">
        <span class="label">Class:</span>
        <span class="dots">................................</span>
      </td>
      <td class="header-cell center-cell">
        <span class="label">Level :</span>
        <span class="value">${escapeHtml(output.grade)}</span>
      </td>
      <td class="header-cell right-cell">
        <span class="label">Duration :</span>
        <span class="value">${escapeHtml(duration)}</span>
      </td>
    </tr>
    <tr>
      <td class="header-cell left-cell">
        <span class="label">Number :</span>
        <span class="dots">................................</span>
      </td>
      <td class="header-cell center-cell">
        <span class="label">School year :</span>
        <span class="value">${escapeHtml(schoolYear)}</span>
      </td>
      <td class="header-cell right-cell">
        <span class="label">Mark</span>
        <span class="value mark-value">&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;/ ${totalPoints}</span>
      </td>
    </tr>
  </tbody>
</table>
${escapedSchoolName ? `<p class="school-name">${escapedSchoolName}</p>` : ""}`
    : ""}
${sectionsHtml}
</body>
</html>`;
}
